import type { ReactNode } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { tokens } from '@configuration/theme'
import AlertIcon from '@icons/Alert.icon'
import CheckIcon from '@icons/Check.icon'

export type InlineMessageVariant = 'error' | 'info' | 'success'

interface VariantStyle {
  border: string
  background: string
  iconColor: string
  icon: ReactNode
}

const VARIANTS: Record<InlineMessageVariant, VariantStyle> = {
  error: {
    border: tokens.rebelliousRed40,
    background: tokens.rebelliousRed10,
    iconColor: tokens.rebelliousRed,
    icon: <AlertIcon />,
  },
  info: {
    border: tokens.versatileViolet20,
    background: tokens.versatileViolet5,
    iconColor: tokens.versatileViolet,
    icon: <AlertIcon />,
  },
  success: {
    border: 'rgba(0,192,77,0.3)',
    background: tokens.generousGreen10Solid,
    iconColor: tokens.generousGreen,
    icon: <CheckIcon />,
  },
}

export interface InlineMessageProps {
  variant?: InlineMessageVariant
  /** Heavier text + taller padding — used for the OIDC linking ceremony banner. */
  strong?: boolean
  sx?: SxProps<Theme>
  dataTestId?: string
  children: ReactNode
}

const InlineMessage = ({
  variant = 'error',
  strong = false,
  sx,
  dataTestId = 'inline-message',
  children,
}: InlineMessageProps) => {
  const v = VARIANTS[variant]
  return (
    <Box
      data-testid={dataTestId}
      // Announce to assistive tech: errors interrupt (assertive), info/success
      // are polite. Banners often appear after a submit round-trip, so without
      // a live role screen readers never hear them.
      role={variant === 'error' ? 'alert' : 'status'}
      sx={[
        {
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          padding: strong ? '16px 14px' : '12px 14px',
          border: `1px solid ${v.border}`,
          background: v.background,
          borderRadius: tokens.radiusMd,
          fontSize: strong ? 14 : 13,
          fontWeight: strong ? 500 : 400,
          color: tokens.businessBlack,
          '& > span[data-icon]': {
            flex: 'none',
            color: v.iconColor,
            marginTop: '1px',
            display: 'inline-flex',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box component='span' data-icon='true' aria-hidden='true'>
        {v.icon}
      </Box>
      <Box>{children}</Box>
    </Box>
  )
}

export default InlineMessage
